/**
 * Table window manager, running on the in-repo panel shell (`panel-shell.ts`).
 *
 * Each Table in the current workspace is rendered as a floating, draggable,
 * resizable panel containing a <data-table>. Geometry (x, y, w, h) is
 * persisted back to the Table record so windows restore on reload.
 *
 * Panels mount into the pan/zoom-transformed canvas viewport (see
 * `panelContainer()`). The app-shell chrome keeps a high z-index so it stays
 * above panels even when they're dragged toward the edges.
 *
 * Panels are draggable anywhere with no boundary clamping — they may sit
 * partly or wholly off-screen. That's intentional: the pan/zoom canvas
 * (touch pan on mobile, right-button drag on desktop) always brings a
 * stray panel back into view, so a hard containment box would only get in
 * the way.
 */

import type { Table, WindowGeometry } from '@easydb/shared';
import { getContext, type AppContext } from '../app-context.js';
import { openTableInfoDialog } from '../dialogs/table-info-dialog.js';
import { initPanZoom } from './panzoom.js';
import { createPanel, type PanelShellEl } from './panel-shell/panel-shell.js';
import { createPanelStack, type PanelStack } from './panel-stack.js';
import { registerPanelStack, unregisterPanelStack } from './panel-stacks.js';
import { setPanZoom, shellViewport } from './shell-viewport.js';
import { queueGeometryWrite } from './geometry-writes.js';
import { countSuffix, importSuffix, IMPORT_PROGRESS_EVENT, VISIBLE_COUNT_EVENT, type ImportProgressDetail, type VisibleCountDetail } from './panel-title.js';
import { forgetRowCount } from '../table/row-count-cache.js';
import { forgetRowRequest } from '../table/visible-request.js';
import { sanitizeGeometry, byAscendingZ } from './geometry.js';
import { tableKind, panelColor, TABLE_KIND_ICONS } from './table-kind.js';
import { createColorButton } from './color-button.js';
import { createFilterRowButton } from './filter-row-button.js';
import { titlebarButtonHidden } from './titlebar-settings.js';
import { readWindowColor, writeWindowColor } from './window-color.js';
import { nextFrontZ } from './front-order.js';
import { registerPanel, unregisterPanel } from './panel-registry.js';
import { initRestack } from './restack.js';
import { FORCE_MINIMIZED } from './boot-flags.js';
import { revealPanel } from './reveal.js';

// Re-exported so existing importers of this module keep working.
export { FORCE_MINIMIZED };
import '../table/data-table.js';
import '../chrome/panel-search.js';
import '../chrome/panel-footer.js';

/** The panel window title: the optional display `title`, else the technical `name`. */
function displayName(t: Table): string {
  return t.title?.trim() ? t.title.trim() : t.name;
}

/** The element panels mount into — the pan/zoom-transformed viewport. */
function panelContainer(): HTMLElement {
  return document.getElementById('easydb-panels-viewport') ?? document.getElementById('easydb-panels') ?? document.body;
}

/**
 * Pin the panel overlay's top/bottom to the app-shell header/footer heights so
 * a maximized window fills exactly the space between them. The overlay's CSS
 * assumes a 48px chrome; this overrides it with the measured heights (which are
 * larger when the header wraps on a narrow window, or under browser zoom).
 */
function syncOverlayInsets(outer: HTMLElement): void {
  const root = document.querySelector('app-shell')?.shadowRoot;
  const header = root?.querySelector('header') as HTMLElement | null;
  const footer = root?.querySelector('footer') as HTMLElement | null;
  if (header) outer.style.top = `${header.offsetHeight}px`;
  if (footer) outer.style.bottom = `${footer.offsetHeight}px`;
}

const panels = new Map<string, PanelShellEl>();
/**
 * Set of table ids whose panels are being closed programmatically — because the
 * table was removed (delete / json-import "Replace entire workspace" / server/
 * gist pull) or hidden from another tab. onclosed reads this to skip its
 * default hide-on-close side-effect: the store already reflects the intent.
 */
const externallyClosed = new Set<string>();
let initialized = false;

/**
 * Persist every open table panel's current rect. Tile/Cascade move panels by
 * writing inline styles, which no jsPanel callback reports, so without this the
 * arranged layout was lost on the next reload (the stored rect still described
 * where the window sat before). Called by the bulk window commands.
 */
export async function persistTablePanelGeometry(): Promise<void> {
  const ctx = await getContext();
  await Promise.all([...panels.keys()].map((id) => saveGeometry(id, ctx)));
}

/**
 * Show a table's window: restore it if minimized, bring it into view, and front
 * it. Used by the command palette's "Go to <table>". Always returns true — a
 * table with no panel is hidden, and un-hiding it opens one.
 */
export function focusTableWindow(tableId: string): boolean {
  const p = panels.get(tableId);
  if (p) {
    // Restore, bring into view (pan on desktop, fill the screen on a phone) and
    // front — the same reveal every targeted window gets, see `reveal.ts`.
    revealPanel(p);
    return true;
  }
  // No panel — the table is hidden (windowGeometry.closed). Un-hide it; the
  // store subscription then opens (and fronts) its panel.
  void unhideTable(tableId);
  return true;
}

/** Clear a table's `closed` flag so the subscription reopens its window. */
async function unhideTable(tableId: string): Promise<void> {
  const ctx = await getContext();
  const t = await ctx.store.tables.findOne(tableId);
  if (!t?.windowGeometry?.closed) return;
  await ctx.store.tables.patch(tableId, {
    windowGeometry: { ...t.windowGeometry, closed: false },
    updatedAt: Date.now(),
  });
}

/**
 * Permanently delete a table and its local rows (source-backed tables keep
 * their rows on the remote). Exposed for the delete-table plugin's trash
 * action; the store subscription closes the panel when the record vanishes.
 */
export async function deleteTable(tableId: string): Promise<void> {
  const ctx = await getContext();
  await deleteTableCascade(tableId, ctx);
}

export async function initWindowManager(): Promise<void> {
  if (initialized) return;
  initialized = true;

  const ctx = await getContext();

  // Pan/zoom over the whole table canvas: touch pan + pinch-zoom on mobile,
  // right-button drag pan on desktop. Both bring a stray (off-screen) panel
  // back into view now that panel dragging is unclamped.
  const outer = document.getElementById('easydb-panels');
  const viewport = document.getElementById('easydb-panels-viewport');
  if (outer && viewport) {
    // The handle lives in `shell-viewport.ts`, which is what panels read — see
    // the note there on why it is not owned by this module.
    setPanZoom(initPanZoom(outer, viewport));
    // Keep the panel overlay aligned to the REAL header/footer heights instead
    // of a hardcoded 48px. The header grows when its buttons wrap (narrow
    // windows) or with larger fonts/zoom; a stale offset left a maximized
    // window's titlebar tucked under the header. Re-sync on any header/footer
    // resize (wrap, plugin buttons) and on window resize.
    const sync = () => syncOverlayInsets(outer);
    sync();
    window.addEventListener('resize', sync);
    const shellRoot = document.querySelector('app-shell')?.shadowRoot;
    const header = shellRoot?.querySelector('header');
    const footer = shellRoot?.querySelector('footer');
    if (typeof ResizeObserver !== 'undefined' && (header || footer)) {
      const ro = new ResizeObserver(sync);
      if (header) ro.observe(header);
      if (footer) ro.observe(footer);
    }
  }

  // Initial population. Open in ascending saved-z order so the panel-shell's
  // session z-counter reproduces the user's last layering — the panel that
  // was on top last session is opened last and ends up on top again.
  const tables = (await ctx.store.tables.find()).filter((t) => t.workspaceId === ctx.workspaceId);
  tables.sort(byAscendingZ);
  for (const t of tables) if (!t.windowGeometry?.closed) openPanel(t, ctx);

  // Reactive sync: open new/reopened tables' panels; close panels whose tables
  // vanished (deleted) OR were hidden (windowGeometry.closed set here or in
  // another tab).
  ctx.store.tables.subscribe((all) => {
    const inWs = all.filter((t) => t.workspaceId === ctx.workspaceId);
    const byId = new Map(inWs.map((t) => [t.id, t]));

    for (const [id, panel] of panels) {
      const t = byId.get(id);
      if (!t || t.windowGeometry?.closed) {
        panels.delete(id);
        unregisterPanel(id);
        // Programmatic close: the table was removed (delete / json-import
        // "Replace entire workspace" / server/gist pull) or hidden elsewhere.
        // Tell onclosed to skip its default hide-on-close side-effect — the
        // store already reflects the intended state.
        externallyClosed.add(id);
        try {
          if (panel.status !== 'closed') panel.close();
        } catch {
          /* already gone */
        }
      }
    }
    const toOpen = inWs.filter((t) => !panels.has(t.id) && !t.windowGeometry?.closed).sort(byAscendingZ);
    for (const t of toOpen) openPanel(t, ctx);
  });

  // A bulk pull (gist sync) inserts tables one at a time, so the
  // reactive `subscribe` above opens each panel in insertion order, not saved-z
  // order (liveQuery fires per write, defeating its sort) — and the same is
  // true across kinds at plain boot (tables always open before views; see
  // `table-list.ts`). `initRestack()` wires the `easydb:restack-windows`
  // listener (fired by gist-sync/json-import after a bulk pull) and runs one
  // merged table+view restack pass immediately, covering the boot case too.
  void initRestack();
}

/** Default size for new (or sanity-reset) panels — matches contentSize below. */
const DEFAULT_W = 720;
const DEFAULT_H = 360;

function openPanel(t: Table, ctx: AppContext): void {
  const panelId = `panel-${cssSafe(t.id)}`;
  const container = panelContainer();
  const g = sanitizeGeometry(t.windowGeometry);
  const startMinimized = FORCE_MINIMIZED || g?.minimized === true;

  // The live <data-table> holds every row in memory, keeps a store subscription
  // open, and (for remote/live tables) fetches rows the moment it mounts. So we
  // mount it lazily: a window that opens minimized gets a bare placeholder and
  // fetches NOTHING until the user expands it — then a fresh grid mounts, shows
  // its progress bar, and loads. Minimizing later detaches the grid again
  // (releasing memory + stopping any polling).
  const makeGrid = (): HTMLElement => {
    const el = document.createElement('data-table');
    (el as HTMLElement & { tableId: string }).tableId = t.id;
    el.style.height = '100%';
    return el;
  };
  // Every table window's content goes through a stack so a visualization can be
  // docked above or below the grid. An EMPTY stack renders its primary child and
  // nothing else — one flex wrapper, no listeners — so a window with no panes
  // behaves exactly as it did before docking existed. See `panel-stack.ts`.
  let stack: PanelStack | null = null;
  const stackKey = `table:${t.id}`;
  const buildStack = (grid: HTMLElement): HTMLElement => {
    stack = createPanelStack();
    stack.setPrimary(grid);
    registerPanelStack(stackKey, stack);
    return stack.root;
  };
  const content: HTMLElement = startMinimized ? document.createElement('div') : buildStack(makeGrid());
  let contentEl: HTMLElement | null = startMinimized ? null : content;

  // Panel title row-count: "<name> (<count>)", or "<name> (<visible>/<total>)"
  // while a search/filter narrows the set. The mounted <data-table> owns the
  // visible-row computation (per-column filters + local + global search) and
  // emits it; we mirror that into the titlebar. A minimized window has no grid
  // and so no count events — the title shows the bare name until it's expanded.
  let lastTitle = displayName(t);
  let lastCount = -1;
  let lastTotal = -1;
  // Set while this table's rows are still being imported. It takes over the
  // title, because during an import the progress IS the interesting count — and
  // the titlebar is the only thing a MINIMIZED window shows, which is how a
  // table imported in the background reports itself.
  let importing: { rows: number; total: number } | null = null;
  const renderTitle = (): void => {
    panel.setHeaderTitle(lastTitle + (importing ? importSuffix(importing.rows, importing.total) : countSuffix(lastCount, lastTotal)));
  };
  const onVisibleCount = (e: Event): void => {
    const d = (e as CustomEvent<VisibleCountDetail>).detail;
    if (d.key !== t.id) return;
    lastCount = d.count;
    lastTotal = d.total;
    renderTitle();
  };
  const onImportProgress = (e: Event): void => {
    const d = (e as CustomEvent<ImportProgressDetail>).detail;
    if (d.tableId !== t.id) return;
    importing = d.done ? null : { rows: d.rows, total: d.total };
    renderTitle();
  };
  document.addEventListener(VISIBLE_COUNT_EVENT, onVisibleCount as EventListener);
  document.addEventListener(IMPORT_PROGRESS_EVENT, onImportProgress as EventListener);

  const unmountContent = (): void => {
    (footer as HTMLElement & { active: boolean }).active = false;
    // Unregister BEFORE detaching: a docked pane's own element is inside this
    // stack, so removing the stack drops the pane too — and a host that is gone
    // must not still look available to the docked-pane reconciler.
    unregisterPanelStack(stackKey);
    stack?.destroy();
    stack = null;
    contentEl?.remove();
    contentEl = null;
  };
  const mountContent = (): void => {
    if (contentEl) return;
    const host = document.getElementById(panelId)?.querySelector('.jsPanel-content') as HTMLElement | null;
    if (!host) return;
    host.replaceChildren(); // drop the minimized placeholder / any stale node
    const el = buildStack(makeGrid());
    host.appendChild(el);
    contentEl = el;
    (footer as HTMLElement & { active: boolean }).active = true;
  };

  const search = document.createElement('panel-search');
  (search as HTMLElement & { tableId: string }).tableId = t.id;

  const footer = document.createElement('panel-footer');
  (footer as HTMLElement & { tableId: string }).tableId = t.id;
  // A minimized window's footer must not subscribe to rows (that fetches for a
  // live table); it's activated when the window is expanded (see mountContent).
  (footer as HTMLElement & { active: boolean }).active = !startMinimized;

  // Closing the window HIDES the table (keeps its data) rather than deleting
  // it — no confirm needed. It's reopened from the command palette
  // ("Go to <table>"); the delete-table button is the only path that removes
  // data.
  const onPanelClosed = async (): Promise<void> => {
    unregisterPanelStack(stackKey);
    stack?.destroy();
    stack = null;
    document.removeEventListener(VISIBLE_COUNT_EVENT, onVisibleCount as EventListener);
    document.removeEventListener(IMPORT_PROGRESS_EVENT, onImportProgress as EventListener);
    // The rect has to be read BEFORE the panel leaves the map: the queued write
    // below runs after any in-flight drag save, by which time there is no shell
    // left to ask.
    const rect = panels.get(t.id)?.persistRect();
    panels.delete(t.id);
    unregisterPanel(t.id);
    // Programmatic close (table deleted/replaced/pulled, or hidden from
    // another tab) — the store already reflects the intended state, so don't
    // re-hide it.
    if (externallyClosed.delete(t.id)) return;
    // User closed the window: persist it as hidden, preserving geometry. Queued
    // like every other geometry write — a drag that lands as the window closes
    // would otherwise patch the whole geometry object on top of this one and
    // drop `closed`, leaving a window the user shut still open on reload.
    await queueGeometryWrite(`table:${t.id}`, async () => {
      const cur = await ctx.store.tables.findOne(t.id);
      if (!cur) return;
      const geom = cur.windowGeometry ?? {
        ...(rect ?? { x: 60, y: 60, w: DEFAULT_W, h: DEFAULT_H }),
        z: 1,
        minimized: false,
        maximized: false,
      };
      await ctx.store.tables.patch(t.id, {
        windowGeometry: { ...geom, closed: true },
        updatedAt: Date.now(),
      });
    });
  };

  const panel = createPanel({
    id: panelId,
    container,
    title: lastTitle,
    logo: TABLE_KIND_ICONS[tableKind(t)],
    // Refreshable tables (source- or origin-backed) read as violet; the shell
    // paints the window AND its dock bar from this one value.
    color: panelColor(t),
    content,
    footerToolbar: footer,
    // Saved g.w/g.h come from offsetWidth/Height (total panel size incl.
    // chrome), so restores use panelSize; new panels size the data area.
    ...(g ? { panelSize: { w: g.w, h: g.h }, position: { x: g.x, y: g.y } } : { contentSize: { w: DEFAULT_W, h: DEFAULT_H }, position: nextCascadePosition() }),
    minimizeTo: '#easydb-minimized-dock',
    viewport: shellViewport(),
    // ?minimize wins over a saved maximized/collapsed state — nothing loads rows
    // on boot.
    boot: {
      minimized: startMinimized,
      maximized: !FORCE_MINIMIZED && g?.maximized === true,
      smallified: !FORCE_MINIMIZED && g?.smallified === true,
    },
    onmoved: () => void saveGeometry(t.id, ctx),
    onresized: () => void saveGeometry(t.id, ctx),
    // Stamp a monotonic front rank; DOM z stays stable in the shell but the
    // persisted rank must survive reloads and merge with views (front-order.ts).
    onfronted: () => void stampFrontOrder(t.id, ctx),
    onstatuschange: (p) => {
      if (p.status === 'minimized') unmountContent();
      else if (p.status === 'normalized' || p.status === 'maximized') mountContent();
      void saveGeometry(t.id, ctx);
    },
    onclosed: () => void onPanelClosed(),
  });

  panels.set(t.id, panel);
  // Registered so the global restack (`restack.ts`) can front this panel
  // without importing this module directly — see `panel-registry.ts`. The
  // `false` suppresses the shell's `onfronted`, so a restack does NOT re-stamp
  // the front rank it is itself ordering by.
  registerPanel(t.id, () => panel.front(undefined, false));

  // Inject the per-table search into the controlbar (right side of the title
  // row, next to min/max/close) so it shares the title bar instead of taking
  // a second header strip — matches the minniDBMax v1 layout.
  const panelEl = document.getElementById(panelId);
  const controlbar = panelEl?.querySelector('.jsPanel-controlbar');
  if (controlbar) controlbar.prepend(search);

  // (i) info button in the titlebar — shown only when the table carries
  // descriptive metadata (Datasette description / source / license / about),
  // which lands asynchronously after a connect/import, so it's toggled
  // reactively as the table record updates.
  let curTable: Table | null = null;
  const infoBtn = document.createElement('button');
  infoBtn.type = 'button';
  infoBtn.title = 'Table info';
  infoBtn.setAttribute('aria-label', 'Table info');
  infoBtn.className = 'eda-info-btn';
  infoBtn.textContent = 'ⓘ';
  infoBtn.style.cssText = 'display:none;background:none;border:0;color:inherit;cursor:pointer;font-size:1rem;line-height:1;padding:0 0.3rem;';
  infoBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!curTable) return;
    openTableInfoDialog(lastTitle, curTable.info ?? {}, {
      source: curTable.source,
      origin: curTable.origin,
    });
  });
  controlbar?.prepend(infoBtn);
  const updateInfoBtn = (table?: Table | null): void => {
    curTable = table ?? null;
    // Show the button when there's descriptive metadata OR a provenance worth
    // explaining (imported snapshot / live connection).
    const has = !!(table?.info || table?.source || table?.origin);
    infoBtn.style.display = has ? 'inline-flex' : 'none';
  };
  updateInfoBtn(t);

  // The user's own titlebar colour, overriding the one this table's KIND gives
  // it. Read after the window is on screen rather than before: `openPanel` is
  // synchronous by design (a window must not wait on a store read to appear), so
  // a chosen colour lands a beat after the default one. See `window-color.ts`.
  let colorOverride: string | null = null;
  const paintChrome = (): void => {
    panel.setHeaderColor(colorOverride ?? panelColor(curTable ?? t));
  };
  const colorBtn = createColorButton({
    current: () => colorOverride,
    onPick: async (color: string | null) => {
      colorOverride = color;
      paintChrome();
      await writeWindowColor(ctx.store, t.id, color);
    },
  });
  controlbar?.prepend(colorBtn);
  // Settings can take either titlebar button away. Read after the window is on
  // screen, like the colour: `openPanel` is synchronous by design, so a button
  // the user switched off is removed a beat later rather than never appearing.
  // Removing beats not-adding — the read is async and the controlbar's order
  // would otherwise depend on which read finished first.
  void titlebarButtonHidden(ctx.api.settings, 'window-color').then((off) => {
    if (off) colorBtn.remove();
  });

  // The funnel: show or hide the filter row for THIS table. Kept on the table
  // record, so it survives a reload and travels with the workspace.
  const filterBtn = createFilterRowButton({
    shown: () => (curTable ?? t).filterRow !== false,
    onToggle: async (next: boolean) => {
      // Written straight to the store rather than to a local flag first: the
      // grid reads the same record through its own subscription, so one write is
      // what moves both. `undefined` for the default, so a table that never
      // touched the toggle carries no field at all.
      await ctx.store.tables.patch(t.id, { filterRow: next ? undefined : false, updatedAt: Date.now() });
    },
  });
  controlbar?.prepend(filterBtn.el);
  void titlebarButtonHidden(ctx.api.settings, 'filter-row').then((off) => {
    if (off) filterBtn.el.remove();
  });
  void readWindowColor(ctx.store, t.id).then((color: string | null) => {
    if (!color) return;
    colorOverride = color;
    paintChrome();
  });

  // A table's kind (normal/imported/referenced/connected) can change at
  // runtime — e.g. a plain table gains an `origin` after an import, or a
  // `source` after a live connect — so the titlebar icon and refreshable
  // colour must react too, exactly like the title does below.
  let lastKind = tableKind(t);

  // Track name changes for the title and info-metadata arrival (cheap — this
  // does NOT fetch rows).
  void ctx.store.tables.subscribe((all) => {
    const cur = all.find((x) => x.id === t.id);
    if (!cur) return;
    updateInfoBtn(cur);
    // `updateInfoBtn` has just set `curTable`, which is what the funnel reads.
    filterBtn.refresh();
    if (displayName(cur) !== lastTitle) {
      lastTitle = displayName(cur);
      renderTitle();
    }
    const kind = tableKind(cur);
    if (kind !== lastKind) {
      lastKind = kind;
      panel.setHeaderLogo(TABLE_KIND_ICONS[kind]);
      // Through `paintChrome`, so a live connect changing the kind does not
      // repaint over a colour the user chose.
      paintChrome();
    }
  });
}

let cascadeIdx = 0;
function nextCascadePosition(): { x: number; y: number } {
  const i = cascadeIdx++;
  return { x: 40 + (i % 8) * 30, y: 80 + (i % 8) * 30 };
}

function saveGeometry(tableId: string, ctx: AppContext): Promise<void> {
  // Serialized against stampFrontOrder: both patch the whole geometry object.
  return queueGeometryWrite(`table:${tableId}`, () => writeGeometry(tableId, ctx));
}

async function writeGeometry(tableId: string, ctx: AppContext): Promise<void> {
  const shell = panels.get(tableId);
  const el = shell ?? document.getElementById(`panel-${cssSafe(tableId)}`);
  if (!el) return;
  const flags = shell?.persistFlags() ?? { minimized: false, maximized: false, smallified: false };
  // The shell owns "which rect belongs in the store" — minimized, maximized and
  // collapsed panels all have a live box that describes something other than
  // their normal geometry (see `persistRect`). Without a shell (the panel is
  // gone from the map) the element's own box is the best we have.
  const rect = shell?.persistRect() ?? {
    x: el.offsetLeft,
    y: el.offsetTop,
    w: el.offsetWidth,
    h: el.offsetHeight,
  };
  try {
    const t = await ctx.store.tables.findOne(tableId);
    const prev = t?.windowGeometry;
    // Under ?minimize the minimized/maximized state is a view override — keep
    // whatever was saved (see WINDOWS.md).
    const minimized = FORCE_MINIMIZED ? (prev?.minimized ?? false) : flags.minimized;
    const maximized = FORCE_MINIMIZED ? (prev?.maximized ?? false) : flags.maximized;
    const smallified = FORCE_MINIMIZED ? (prev?.smallified ?? false) : flags.smallified;
    const geom: WindowGeometry = {
      ...rect,
      // Preserve the front-order timestamp written by stampFrontOrder.
      z: prev?.z ?? 0,
      minimized,
      maximized,
      smallified,
    };
    await ctx.store.tables.patch(tableId, {
      windowGeometry: geom,
      updatedAt: Date.now(),
    });
  } catch {
    // Table might have just been deleted — ignore.
  }
}

/**
 * Save a "front rank" into windowGeometry.z. The shell's own DOM z-index is
 * session-monotonic (never renormalized — see panel-shell.ts) but resets on
 * every reload, so it can't be persisted directly; each front fires once and
 * the relative ordering follows from the strictly-increasing rank instead.
 * `nextFrontZ()` (`front-order.ts`) is shared with view windows so a table and
 * a view fronted moments apart still compare correctly against each other.
 */
function stampFrontOrder(tableId: string, ctx: AppContext): Promise<void> {
  // Serialized against saveGeometry — see geometry-writes.ts.
  return queueGeometryWrite(`table:${tableId}`, () => writeFrontOrder(tableId, ctx));
}

async function writeFrontOrder(tableId: string, ctx: AppContext): Promise<void> {
  try {
    const t = await ctx.store.tables.findOne(tableId);
    if (!t) return;
    // Nothing stored yet: take the panel's own rect rather than a constant. The
    // old placeholder used the CONTENT size as if it were the PANEL size, so a
    // window whose first-ever write was this one restored at 0,0 and too small.
    const geom = t.windowGeometry ?? {
      ...(panels.get(tableId)?.persistRect() ?? { x: 0, y: 0, w: DEFAULT_W, h: DEFAULT_H }),
      z: 0,
      minimized: false,
      maximized: false,
    };
    await ctx.store.tables.patch(tableId, {
      windowGeometry: { ...geom, z: nextFrontZ() },
      updatedAt: Date.now(),
    });
  } catch {
    /* table may have just been deleted — ignore */
  }
}

async function deleteTableCascade(tableId: string, ctx: AppContext): Promise<void> {
  // Source-backed tables (e.g. a live Datasette connection) keep their rows on
  // the remote, not in the local store — `rows(tableId)` routes to the remote
  // provider. We must NOT cascade row-deletes there: it would issue remote
  // DELETEs (or, for a read-only connection, throw SourceReadOnlyError before
  // `tables.remove` runs — leaving the table behind so its panel reappears).
  // Closing the window just drops the local Table record (disconnects).
  const table = await ctx.store.tables.findOne(tableId);
  if (!table?.source) {
    const rowColl = ctx.store.rows(tableId);
    const rows = await rowColl.find();
    await rowColl.bulkRemove(rows.map((r) => r.id));
  }
  await ctx.store.tables.remove(tableId);
  // The remembered size and the published request go with the table. A new table
  // reusing this id is not possible (ids are UUIDs), so both would simply sit
  // there forever.
  forgetRowCount(tableId);
  forgetRowRequest(tableId);
}

function cssSafe(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, '_');
}

/**
 * The table whose window contains `node`, or null when `node` is anywhere else
 * (the canvas, the chrome, a view window).
 *
 * Asks the panel map rather than parsing the DOM id: `cssSafe` is lossy, so a
 * `panel-<id>` attribute cannot be turned back into the table id it came from.
 * Used by the drop handlers to tell "dropped on THIS table" from "dropped on the
 * app".
 */
export function tableIdAtNode(node: EventTarget | null): string | null {
  return tablePanelEntryAtNode(node)?.[0] ?? null;
}

/**
 * The same lookup, returning the window ELEMENT — what a drag needs in order to
 * highlight the window a drop would land in.
 *
 * Shares one loop with `tableIdAtNode` on purpose: the highlight has to name the
 * same window the drop will use, and two lookups could disagree about a panel that
 * is opening or closing. A highlight that lies about where a file will land is worse
 * than no highlight.
 */
export function tablePanelAtNode(node: EventTarget | null): HTMLElement | null {
  return tablePanelEntryAtNode(node)?.[1] ?? null;
}

function tablePanelEntryAtNode(node: EventTarget | null): [string, PanelShellEl] | null {
  if (!(node instanceof Node)) return null;
  for (const entry of panels) {
    if (entry[1].contains(node)) return entry;
  }
  return null;
}
